import { IProject } from "@/interfaces/project.interface";
import { useAppDispatch, useAppSelector } from "@/store/hooks/hooks";
import { RootState } from "@/store/store";
import { useEffect, useRef, useState } from "react";
import { setSelectedProject } from "@/store/slices/projectSlice";
import { useLocation } from "react-router-dom";
import { ChevronDown, LayoutGrid, SearchIcon } from "lucide-react";

const ProjectDropdown: React.FC = () => {
  const { projects, selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [isSettings, setIsSettings] = useState(false);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const savedProjectId = localStorage.getItem("selectedProjectId");
    if (savedProjectId && projects.length > 0) {
      const savedProject = projects?.find((p) => p.id === savedProjectId);
      if (savedProject && savedProject.id !== selectedProject?.id) {
        dispatch(setSelectedProject(savedProject as IProject));
        localStorage.setItem("selectedProjectId", savedProject.id!);
      }
    }
  }, [projects]);

  useEffect(() => {
    if (location.pathname.includes("/settings")) {
      setIsSettings(true);
    } else {
      setIsSettings(false);
    }
  }, [location.pathname]);

  const handleSelect = (project: IProject) => {
    dispatch(setSelectedProject(project));
    localStorage.setItem("selectedProjectId", project.id!);

    // remove filters from storage if project changes
    localStorage.removeItem("selectedLevel");
    localStorage.removeItem("selectedUsersFilter");
    localStorage.removeItem("selectedStatus");
    setIsOpen(false);
    //window.location.reload();
  };

  const filteredProjects = projects.filter((project) =>
    project?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={dropdownRef} className={`relative inline-block text-left`}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2 ${
          isSettings ? "" : "text-white"
        } rounded-md font-medium text-base`}
      >
        {selectedProject ? (
          <>
            <LayoutGrid className="w-6 h-6" />
            {selectedProject.name}
          </>
        ) : (
          <>
            <img
              src="./images/grid-view.svg"
              alt="grid icon"
              className="w-6 h-6"
            />
            Select a Project
          </>
        )}
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          className={`absolute mt-2 left-[-5px] top-[25px] rounded-[9px] border-[2px] ${
            isSettings
              ? "border-gray bg-[#FFF]"
              : "border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
          } min-w-[304px] z-[999]`}
        >
          <div className="px-4 py-3 border-b border-[414148] mb-3">
            <div className="relative flex items-center gap-2">
              <SearchIcon color="#64748B" className="w-4 h-4" />
              <input
                type="search"
                placeholder="Search Project"
                className={`w-full pl-0 px-3 p-2 bg-transparent ${
                  isSettings ? "" : "placeholder:text-[#94A3B8] text-white"
                } rounded-md outline-none`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <ul className="max-h-60 overflow-y-auto ">
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project) => (
                <li
                  key={project.id}
                  className={`flex items-center gap-2 p-3 cursor-pointer ${
                    isSettings
                      ? "text-gray hover:bg-gray-200"
                      : "text-white hover:bg-gray-700"
                  } text-sm border border-solid border-white/10 border-l-0 border-r-0 border-t-0 last-of-type:border-b-0`}
                  onClick={() => handleSelect(project as IProject)}
                >
                  <img
                    src={
                      isSettings
                        ? "../images/alec_small_blue.png"
                        : "./images/alec-blue-icon.png"
                    }
                    alt="icon"
                    className="w-6 h-6"
                  />
                  <div className="flex-1">
                    <h4>{project.name}</h4>
                    <span
                      className={`${
                        isSettings ? "text-gray" : "text-[#B3B3B3]"
                      }`}
                    >
                      {project.code}
                    </span>
                  </div>
                </li>
              ))
            ) : (
              <li className="p-3 text-white text-center">No projects found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProjectDropdown;
